import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Rocket, Check, Video, FileText, LayoutGrid, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { WaIcon } from "./BrandIcons";

// Deadline du compte à rebours : 6 novembre 2026, 00h00 heure de Paris (UTC+1)
const LAUNCH_AT = new Date("2026-11-06T00:00:00+01:00").getTime();

const pad = (n) => String(n).padStart(2, "0");

const CHIPS = [
  { Icon: Video, key: "chip1" },
  { Icon: FileText, key: "chip2" },
  { Icon: LayoutGrid, key: "chip3" },
  { Icon: Globe, key: "chip4" },
];

const CHECK_KEYS = ["check1", "check2", "check3", "check4"];

export const PrelaunchCard = ({ invitedBy, count }) => {
  const { lang, t } = useLang();
  const c = t.prelaunch;
  const [left, setLeft] = useState(() => Math.max(0, LAUNCH_AT - Date.now()));

  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, LAUNCH_AT - Date.now())), 1000);
    return () => clearInterval(id);
  }, []);

  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);

  return (
    <div
      className="overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600/80 via-purple-500/50 to-signal/80 p-px shadow-[0_0_70px_-18px_rgba(139,92,246,0.55)]"
      data-testid="prelaunch-card"
    >
      <div className="overflow-hidden rounded-[23px] bg-night-2/95 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-gradient-to-r from-violet-600 via-purple-600 to-tactical px-5 py-2 sm:px-6">
          <span className="flex items-center gap-2.5 font-display text-xs font-bold tracking-[0.16em] text-white sm:text-sm" data-testid="prelaunch-brand">
            <Rocket size={15} className="shrink-0" />
            {c.barBrand}
          </span>
          <span
            className="flex items-center gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white sm:text-[11px]"
            data-testid="prelaunch-countdown-badge"
          >
            <span className="h-2 w-2 rounded-full bg-signal animate-pulse-soft" />
            {left > 0 ? c.countdownLabel : c.launched}
            {left > 0 && (
              <span className="rounded-md bg-night/45 px-2 py-0.5 tabular-nums" data-testid="prelaunch-countdown">
                {pad(d)}J {pad(h)}:{pad(m)}:{pad(s)}
              </span>
            )}
          </span>
        </div>

        <div className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[minmax(0,370px)_1fr] lg:gap-7 lg:p-6">
          <div className="relative aspect-video lg:aspect-auto">
            <video
              poster="/hero-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              data-testid="prelaunch-video"
              className="absolute inset-0 h-full w-full rounded-2xl object-cover drop-shadow-[0_0_35px_rgba(139,92,246,0.35)]"
            >
              <source src="/hero-video.mp4" type="video/mp4" />
              <source src="/hero-video.webm" type="video/webm" />
            </video>
          </div>

          <div className="min-w-0">
            {invitedBy && (
              <div
                className="mb-4 inline-flex flex-wrap items-center gap-2 rounded-full border border-signal2/40 bg-signal2/10 px-4 py-2"
                data-testid="hero-invited-by"
              >
                <span className="text-xs text-ink-muted">{t.hero.invitedBy}</span>
                <span className="font-mono text-xs font-bold text-signal2">@{invitedBy}</span>
                {count !== null && (
                  <>
                    <span className="h-3 w-px bg-white/20" />
                    <span className="font-mono text-xs font-bold text-signal" data-testid="hero-future-position">
                      {t.hero.youWillBe}{" "}
                      <motion.span
                        key={count}
                        initial={{ opacity: 0.2, scale: 1.25 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="inline-block"
                      >
                        #{count + 1}
                      </motion.span>
                    </span>
                  </>
                )}
              </div>
            )}

            <h1 className="font-display font-bold leading-[1.1] tracking-tight">
              <span className="block text-[clamp(1.1rem,2vw,1.55rem)] text-signal text-glow-signal" data-testid="prelaunch-title">
                {c.titleA}
              </span>
              <span className="block text-[clamp(1.1rem,2vw,1.55rem)] text-violet-400">{c.titleB}</span>
            </h1>

            <p className="mt-2 text-[13px] font-bold uppercase tracking-wide text-ink sm:text-sm" data-testid="prelaunch-subtitle">
              {c.subtitle}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted" data-testid="prelaunch-body">
              {c.body}
            </p>

            <ul className="mt-3.5 space-y-1.5">
              {CHECK_KEYS.map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-[13px] text-ink" data-testid={`prelaunch-${k}`}>
                  <Check size={13} strokeWidth={3} className="mt-0.5 shrink-0 text-signal2" />
                  <span>{c[k]}</span>
                </li>
              ))}
            </ul>

            <p
              className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-violet-400"
              data-testid="prelaunch-reveal-title"
            >
              {c.reveal}
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {CHIPS.map(({ Icon, key }) => (
                <div
                  key={key}
                  className="flex items-center gap-2.5 rounded-xl border border-violet-500/25 bg-night-3/50 px-3 py-2"
                  data-testid={`prelaunch-${key}`}
                >
                  <Icon size={13} className="shrink-0 text-violet-400" />
                  <span className="text-[11px] font-medium leading-snug text-ink-muted">{c[key]}</span>
                </div>
              ))}
            </div>

            <a
              href="#register"
              data-testid="prelaunch-cta-button"
              className="mt-4 flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-tactical via-signal to-tactical bg-[length:200%_100%] bg-left px-8 py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-night transition-[background-position,box-shadow,transform] duration-300 hover:scale-[1.01] hover:bg-right hover:glow-signal"
            >
              <Rocket size={14} />
              {c.cta}
            </a>

            <p className="mt-2.5 flex items-center justify-center gap-2 text-center text-xs text-ink-muted" data-testid="prelaunch-share-note">
              <Globe size={12} className="shrink-0 text-signal2" />
              {c.share}
            </p>
            {lang === "fr" && (
              <a
                href="https://chat.whatsapp.com/IQw8ZR4gsBIBzh1xJnNyk1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="mx-auto mt-2.5 flex w-fit items-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-2 text-xs font-bold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-night"
              >
                <WaIcon size={14} />
                Rejoindre le canal francophone
              </a>
            )}

            <p className="mt-3 text-center text-[10px] leading-relaxed text-ink-subtle" data-testid="prelaunch-disclaimer">
              {c.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrelaunchCard;
